import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { SectionNames } from './PortalState/SectionDataMetaData';
import DividerComponent from "../Components/DividerComponent";
import SettingSelectField from "../../../components/SettingSelectField";
import SettingTextField from "../../../components/SettingTextField";

const PAYMENT_GATEWAYS = [
    { label: "Stripe", value: "stripe" },
    { label: "Dojo", value: "dojo" },
    { label: "Ryft", value: "ryft" },
];

const TABLE_ORDER_GATEWAYS = [
    { label: "Stripe", value: "stripe" },
    { label: "Dojo", value: "dojo" },
    { label: "Direct", value: "direct" },
    { label: "Ryft", value: "ryft" },
];

const PaymentSettingsTab = ({ 
    data, 
    handleChange, 
    styles, 
    errors 
}) => {
    const handleSelectChange = (fieldName, value) => {
        handleChange(SectionNames.PaymentSettingsTab, fieldName, value);
    };

    const handleInputChange = (fieldName, value) => {
        handleChange(SectionNames.PaymentSettingsTab, fieldName, value);
    };

    return (
        <>
            <Grid item container xs={12} mt={3}>
                <Typography className={styles?.textInputLabel}>
                    Payment Gateway Settings
                </Typography>
            </Grid>

            {/* Stripe Destination Account ID */}
            <Grid container item xs={12} mt={2} mb={2}>
                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Stripe Destination Account ID
                        </Typography>
                    </Box>
                    <Box>
                        <SettingTextField
                            fullWidth
                            variant="filled"
                            placeholder="Enter Stripe account ID"
                            value={data?.stripeDestinationAccountId || ''}
                            setValue={(value) => handleInputChange('stripeDestinationAccountId', value)}
                            error={!!errors?.get?.('stripeDestinationAccountId')}
                            helperText={errors?.get?.('stripeDestinationAccountId')}
                            border={"1px solid #AAA"}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Ryft Destination Account ID */}
            <Grid container item xs={12} mt={2} mb={2}>
                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Ryft Destination Account ID
                        </Typography>
                    </Box>
                    <Box>
                        <SettingTextField
                            fullWidth
                            variant="filled"
                            placeholder="Enter Ryft account ID"
                            value={data?.ryftDestinationAccountId || ''}
                            setValue={(value) => handleInputChange('ryftDestinationAccountId', value)}
                            error={!!errors?.get?.('ryftDestinationAccountId')}
                            helperText={errors?.get?.('ryftDestinationAccountId')}
                            border={"1px solid #AAA"}
                        />
                    </Box>
                </Grid>
            </Grid>

            <DividerComponent />

            {/* Online Order Settings */}
            <Grid item container xs={12} mt={3}>
                <Typography className={styles?.textInputLabel}>
                    Online Order Settings
                </Typography>
            </Grid>
            
            <Grid container item xs={12} mt={2} mb={2} spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Default Gateway
                        </Typography>
                    </Box>
                    <Box>
                        <SettingSelectField
                            page={"paymentSettings"}
                            value={data?.onlineOrderDefaultGateway || 'stripe'}
                            setValue={(val) => handleSelectChange('onlineOrderDefaultGateway', val)}
                            border={"1px solid #AAA"}
                            placeholder={"Select gateway"}
                            required={true}
                            variant={"filled"}
                            menuList={PAYMENT_GATEWAYS}
                            error={errors?.get?.('onlineOrderDefaultGateway')}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Application Fee (%)
                        </Typography>
                    </Box>
                    <Box>
                        <SettingTextField
                            fullWidth
                            variant="filled"
                            type="number"
                            placeholder="0"
                            value={data?.onlineOrderApplicationFeePercent || 0}
                            setValue={(value) => handleInputChange('onlineOrderApplicationFeePercent', parseFloat(value) || 0)}
                            error={!!errors?.get?.('onlineOrderApplicationFeePercent')}
                            helperText={errors?.get?.('onlineOrderApplicationFeePercent')}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            border={"1px solid #AAA"}
                        />
                    </Box>
                </Grid>
            </Grid>

            <DividerComponent />

            {/* Table Order Settings */}
            <Grid item container xs={12} mt={3}>
                <Typography className={styles?.textInputLabel}>
                    Table Order Settings
                </Typography>
            </Grid>
            
            <Grid container item xs={12} mt={2} mb={2} spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Default Gateway
                        </Typography>
                    </Box>
                    <Box>
                        <SettingSelectField
                            page={"paymentSettings"}
                            value={data?.tableOrderDefaultGateway || 'direct'}
                            setValue={(val) => handleSelectChange('tableOrderDefaultGateway', val)}
                            border={"1px solid #AAA"}
                            placeholder={"Select gateway"}
                            required={true}
                            variant={"filled"}
                            menuList={TABLE_ORDER_GATEWAYS}
                            error={errors?.get?.('tableOrderDefaultGateway')}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Application Fee (%)
                        </Typography>
                    </Box>
                    <Box>
                        <SettingTextField
                            fullWidth
                            variant="filled"
                            type="number"
                            placeholder="0"
                            value={data?.tableOrderApplicationFeePercent || 0}
                            setValue={(value) => handleInputChange('tableOrderApplicationFeePercent', parseFloat(value) || 0)}
                            error={!!errors?.get?.('tableOrderApplicationFeePercent')}
                            helperText={errors?.get?.('tableOrderApplicationFeePercent')}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            border={"1px solid #AAA"}
                        />
                    </Box>
                </Grid>
            </Grid>

            <DividerComponent />

            {/* Table Booking Deposit Settings */}
            <Grid item container xs={12} mt={3}>
                <Typography className={styles?.textInputLabel}>
                    Table Booking Deposit Settings
                </Typography>
            </Grid>
            
            <Grid container item xs={12} mt={2} mb={2} spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Default Gateway
                        </Typography>
                    </Box>
                    <Box>
                        <SettingSelectField
                            page={"paymentSettings"}
                            value={data?.tableBookingDepositDefaultGateway || 'stripe'}
                            setValue={(val) => handleSelectChange('tableBookingDepositDefaultGateway', val)}
                            border={"1px solid #AAA"}
                            placeholder={"Select gateway"}
                            required={true}
                            variant={"filled"}
                            menuList={PAYMENT_GATEWAYS}
                            error={errors?.get?.('tableBookingDepositDefaultGateway')}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Application Fee (%)
                        </Typography>
                    </Box>
                    <Box>
                        <SettingTextField
                            fullWidth
                            variant="filled"
                            type="number"
                            placeholder="0"
                            value={data?.tableBookingDepositApplicationFeePercent || 0}
                            setValue={(value) => handleInputChange('tableBookingDepositApplicationFeePercent', parseFloat(value) || 0)}
                            error={!!errors?.get?.('tableBookingDepositApplicationFeePercent')}
                            helperText={errors?.get?.('tableBookingDepositApplicationFeePercent')}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            border={"1px solid #AAA"}
                        />
                    </Box>
                </Grid>
            </Grid>

            <DividerComponent />

            {/* Gift Card Settings */}
            <Grid item container xs={12} mt={3}>
                <Typography className={styles?.textInputLabel}>
                    Gift Card Settings
                </Typography>
            </Grid>
            
            <Grid container item xs={12} mt={2} mb={2} spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Default Gateway
                        </Typography>
                    </Box>
                    <Box>
                        <SettingSelectField
                            page={"paymentSettings"}
                            value={data?.giftCardDefaultGateway || 'stripe'}
                            setValue={(val) => handleSelectChange('giftCardDefaultGateway', val)}
                            border={"1px solid #AAA"}
                            placeholder={"Select gateway"}
                            required={true}
                            variant={"filled"}
                            menuList={PAYMENT_GATEWAYS}
                            error={errors?.get?.('giftCardDefaultGateway')}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box mb={1}>
                        <Typography className={styles?.textInputLabel}>
                            Application Fee (%)
                        </Typography>
                    </Box>
                    <Box>
                        <SettingTextField
                            fullWidth
                            variant="filled"
                            type="number"
                            placeholder="0"
                            value={data?.giftCardApplicationFeePercent || 0}
                            setValue={(value) => handleInputChange('giftCardApplicationFeePercent', parseFloat(value) || 0)}
                            error={!!errors?.get?.('giftCardApplicationFeePercent')}
                            helperText={errors?.get?.('giftCardApplicationFeePercent')}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            border={"1px solid #AAA"}
                        />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default React.memo(PaymentSettingsTab);